<template>
  <transition name="fade">
    <div
      class="invite-bar"
      v-if="visible"
    >
      <div class="invite-content">
        <div class="invite-info">
          <IconCallAnsweredIn v-if="isInCall" />
          <IconCallAnsweredOut v-if="isOutCall" />
          <MessageInIcon v-if="isInText" />
          <MessageOutIcon v-if="isOutText" />
          <q-icon
            name="mdi-email-receive-outline"
            size="sm"
            color="primary"
            v-if="isInMail"
          />

          <q-icon
            name="mdi-email-send-outline"
            size="sm"
            color="primary"
            v-if="isOutMail"
          />
        </div>

        <div class="invite-info">
          <p
            class="contact-number"
            :class="{ 'no-select': isAnonymizationEnabled }"
            v-if="contactNumber"
          >
            {{ contactNumber }}
          </p>

          <p
            class="contact-name"
            :class="{ 'no-select': isAnonymizationEnabled }"
          >
            {{ contactName }}
          </p>
        </div>

        <div class="invite-actions">
          <button
            data-testid="oferta-aceitar"
            class="action-button accept"
            @click="acceptInvite"
          >
            Aceitar
          </button>
          <button
            data-testid="oferta-rejeitar"
            class="action-button reject"
            @click="rejectInvite"
          >
            Rejeitar
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapGetters } from 'vuex'
import events from '@/libs/events'
import MessageOutIcon from '@/assets/icons/messageOut.svg'
import MessageInIcon from '@/assets/icons/messageIn.svg'
import IconCallAnsweredOut from '@/assets/icons/call-answered-out.svg'
import IconCallAnsweredIn from '@/assets/icons/call-answered-in.svg'
import { anonymizePhone } from '@/components/utils/phones'
import { getContactNumber } from '@/libs/helpers'

export default {
  components: {
    MessageOutIcon,
    MessageInIcon,
    IconCallAnsweredOut,
    IconCallAnsweredIn
  },

  data () {
    return {
      attendance: {},
      visible: false
    }
  },

  mounted () {
    const vm = this

    vm.busEvents = events()
    vm.busEvents.on('open-attendance-offer', vm.onOpenAttendanceOffer)
    vm.busEvents.on('cancel-attendance-offer', vm.onCancelAttendanceOffer)
    vm.busEvents.on('answer-attendance-offer', vm.onCancelAttendanceOffer)
    vm.busEvents.on('answer-attendance-offer-text', vm.onCancelAttendanceOffer)

    vm.interval = setInterval(() => {
      if (!vm.visible) return
      if (!vm.attendance?.id) return
      if (!vm.attendance.offerAt) return
      if (vm.attendance.offerAt >= (Date.now() - 35000)) return
      vm.onCancelAttendanceOffer()
    }, 1000)
  },

  beforeDestroy () {
    const vm = this
    clearInterval(vm.interval)
    vm.busEvents.removeListener('open-attendance-offer', vm.onOpenAttendanceOffer)
    vm.busEvents.removeListener('cancel-attendance-offer', vm.onCancelAttendanceOffer)
    vm.busEvents.removeListener('answer-attendance-offer', vm.onCancelAttendanceOffer)
    vm.busEvents.removeListener('answer-attendance-offer-text', vm.onCancelAttendanceOffer)
  },

  computed: {
    ...mapGetters({
      queueProperties: 'agentModule/queueProperties'
    }),

    contactNumber () {
      const vm = this
      const isText = vm.attendance.channel === 'TEXT'
      const rawNumber = isText ? getContactNumber(vm.attendance) : (vm.attendance.contact ?? '')
      return vm.displayPhone(rawNumber)
    },

    contactName () {
      const vm = this
      return vm.attendance.contactName || this.displayPhone(vm.attendance.contact)
    },

    isInCall () {
      const vm = this
      return vm.attendance.channel === 'VOICE' && vm.attendance.direction === 'IN'
    },

    isOutCall () {
      const vm = this
      return vm.attendance.channel === 'VOICE' && vm.attendance.direction !== 'IN'
    },

    isInText () {
      const vm = this
      return vm.attendance.channel === 'TEXT' && vm.attendance.direction === 'IN'
    },

    isOutText () {
      const vm = this
      return vm.attendance.channel === 'TEXT' && vm.attendance.direction !== 'IN'
    },

    isInMail () {
      const vm = this
      return vm.attendance.channel === 'MAIL' && vm.attendance.direction === 'IN'
    },

    isOutMail () {
      const vm = this
      return vm.attendance.channel === 'MAIL' && vm.attendance.direction !== 'IN'
    },

    queueId () {
      return this.attendance.queue ?? this.attendance.queueId
    },

    isAnonymizationEnabled () {
      if (this.attendance.anonymizePhone === true) return true
      if (!this.queueId) return false
      const queueProps = this.queueProperties(this.queueId)
      return queueProps?.anonymizePhone === 'yes'
    }
  },

  methods: {
    displayPhone (phone) {
      if (!phone) return phone
      if (this.isAnonymizationEnabled) return anonymizePhone(phone)
      return phone
    },

    onOpenAttendanceOffer (attendance) {
      const vm = this

      const canShow = !vm.visible || vm.attendance.channel !== 'VOICE' || !vm.attendance.offerAt || vm.attendance.offerAt <= (Date.now() - 30000)
      if (!canShow) return

      vm.attendance = Object.assign({}, attendance, { offerAt: Date.now() })
      vm.visible = true
    },

    onCancelAttendanceOffer (cancel) {
      const vm = this
      const cancelOffer = vm.attendance.id === cancel.id

      if (cancelOffer) {
        vm.visible = false
        vm.attendance = {}
      }
    },

    answerOffer (accepted) {
      const vm = this
      const isVoice = vm.attendance.channel === 'VOICE'
      const channel = isVoice ? 'answer-attendance-offer' : 'answer-attendance-offer-text'
      vm.busEvents.emit(channel, Object.assign({}, vm.attendance, { accepted }))
    },

    acceptInvite () {
      this.answerOffer(true)
    },

    rejectInvite () {
      this.answerOffer(false)
    }
  },

  watch: {
    $route (to) {
      const vm = this
      if (!vm.attendance?.id) return
      const forceShow = !(to.name === 'ROUTE_ATTENDANCE_ITEM' && to.params.attendanceId === vm.attendance.id && vm.attendance.channel === 'VOICE')
      if (vm.visible !== forceShow) vm.visible = forceShow
    }
  }
}
</script>

<style lang="scss" scoped>
$background-start: #ffffff;
$background-end: #f7f7f7;
$border-color: #d1d1d6;
$shadow-color: rgba(0, 0, 0, 0.15);
$accept-color: #007aff;
$reject-color: #ff3b30;
$text-color: #1c1c1e;
$subtext-color: #565663;
$font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;

.invite-bar {
  position: fixed;
  top: 50px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(180deg, $background-start, $background-end);
  border: 1px solid $border-color;
  border-radius: 12px;
  box-shadow: 0 4px 12px $shadow-color;
  padding: 8px 12px;
  min-width: 250px;
  max-width: 90%;
  z-index: 1000;
  font-family: $font-family;
  color: $text-color;
  overflow: hidden;

  .invite-content {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: 8px;
  }

  .invite-info {
    flex: 1 1 auto;
    min-width: 0; // Permite que o texto seja truncado sem ultrapassar o container
    display: flex;
    flex-direction: column;
    line-height: 1.2;
    overflow: hidden;

    .contact-number,
     .contact-name{
      margin: 0;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      &.no-select {
        user-select: none;
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
      }
    }

    .contact-number {
      font-size: 13px;
      font-weight: 500;
      color: $subtext-color;

      &.no-select {
        user-select: none;
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
      }
    }

    .contact-name {
      font-size: 17px;
      font-weight: 600;
      margin-top: 2px;
    }
  }

  .invite-actions {
    display: flex;
    flex-direction: column; // Botões empilhados verticalmente
    gap: 4px;
    flex-shrink: 0; // Garante que os botões não encolham

    .action-button {
      padding: 4px 8px;
      background-color: #ffffff;
      backdrop-filter: blur(4px);
      border: 1px solid transparent;
      border-radius: 8px;
      font-size: 12px;
      font-weight: 500;
      cursor: pointer;
      transition: background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
      outline: none;

      &.accept {
        border-color: $accept-color;
        color: $accept-color;

        &:hover {
          background-color: rgba(0, 122, 255, 0.05);
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        &:active {
          background-color: rgba(0, 122, 255, 0.1);
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
        }
      }

      &.reject {
        border-color: $reject-color;
        color: $reject-color;

        &:hover {
          background-color: rgba(255, 59, 48, 0.05);
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        &:active {
          background-color: rgba(255, 59, 48, 0.1);
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
        }
      }
    }
  }
}

@keyframes progressAnimation {
  0% {
    width: 0%;
    background-color: rgb(7, 82, 25);
  }

  100% {
    width: 100%;
    background-color: #ff3b30;
  }
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>
